import React, { useState, useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { Card, Text, Box, Button, Flex, NumberFormatter } from "@mantine/core";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";

import DealerVerification from "./DealerVerification";
import { VehicleImage } from "@components/RideChallenge";
import OdometerValueMismatch from "./OdometerValueMismatch";
import DealerWarnings from "./DealerWarnings";
import DealerMode from "./DealerMode";
import OdometerMismatchModal from "./OdometerMismatchModal";
import OdometerForm from "./OdometerForm";

export default function VehicleManagement({
  vehicles = [],
  additionalReqData,
  fetchData,
  verificationCode,
  codeVerificationData,
  setUnverifiedView,
  setDashboardView,
  allowMileageEntry = true,
  isVehicleDataEffort = false,
}) {
  const { t } = useTranslation();
  return (
    <div>
      {!allowMileageEntry && (
        <Text fw={600} c="yellow" align="center" mb="lg">
          {t("microsite:max_mileage_reached")}
        </Text>
      )}
      {vehicles.map((m) => (
        <Box key={m.id} mb="lg">
          <VehicleItem
            info={m}
            additionalReqData={additionalReqData}
            setDashboardView={setDashboardView}
            fetchData={() => {
              fetchData();
            }}
            verificationCode={verificationCode}
            codeVerificationData={codeVerificationData}
            setUnverifiedView={setUnverifiedView}
            allowMileageEntry={allowMileageEntry}
            isVehicleDataEffort={isVehicleDataEffort}
          />
        </Box>
      ))}
    </div>
  );
}

const VehicleItem = ({
  info,
  fetchData,
  additionalReqData,
  verificationCode,
  codeVerificationData,
  setUnverifiedView,
  setDashboardView,
  allowMileageEntry = true,
  isVehicleDataEffort = false,
}) => {
  const { t } = useTranslation();
  const [isOpen, setOpen] = useState(false);

  const odometerRolled = info.odometer_rolled;

  const currentOdometerReading = odometerRolled
    ? info.current_odometer_reading > 1000000
      ? info.current_odometer_reading - 1000000
      : info.current_odometer_reading
    : info.current_odometer_reading;

  return (
    <Card
      style={{
        background: "var(--mantine-color-dark-9)",
        borderColor: "var(--mantine-color-dark-8)",
      }}
    >
      <Flex align="start" gap="md">
        <div>
          <VehicleImage
            userVehicleId={info.id}
            onSuccess={fetchData}
            height={60}
            width={60}
            url={
              info.assets.length
                ? info.assets[info.assets.length - 1].filename_url
                : null
            }
          />
        </div>
        <div style={{ flexGrow: 1 }}>
          <Text c="white" size="md" fw={600}>
            {info.year} {info.make} {info.model}{" "}
            {info.series && <>{info.series}</>}
          </Text>
          {info.current_odometer_reading !== undefined && (
            <Text c="dimmed" size="sm">
              {t("mixer:vehicle:odometer")}:{" "}
              <NumberFormatter
                thousandSeparator
                value={currentOdometerReading}
              />{" "}
              {info.use_kilometers ? "km" : "mi"}
            </Text>
          )}
          {!isVehicleDataEffort && (
            <Text c="dimmed" size="sm">
              <NumberFormatter thousandSeparator value={info.odometer_total} />{" "}
              mi {t("common:verified")?.toLowerCase()}
            </Text>
          )}
          {!isOpen && info.has_unverified_readings && allowMileageEntry && (
            <Text c="yellow" size="sm" mt="sm" fw={600}>
              {t("mixer:vehicle:unverified_reading_text", {
                units: info.use_kilometers ? "kilometers" : "miles",
              })}
              .
            </Text>
          )}
        </div>
      </Flex>
      <Box mt={allowMileageEntry ? 0 : "sm"}>
        {isOpen ? (
          <motion.div
            initial={{ opacity: 0, y: -100, scale: 0.5 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            // exit={{ opacity: 0, y: 200, scale: 1.2 }}
            transition={{ duration: 0.6, type: "spring" }}
          >
            <AddNewReading
              fetchData={() => {
                setDashboardView();
                setOpen(false);
              }}
              odometerRolled={odometerRolled}
              codeVerificationData={codeVerificationData}
              currentOdometer={info.current_odometer_reading}
              reqData={{
                ...additionalReqData,
                make: info.make,
                model: info.model,
                series: info.series,
                year: info.year,
                vin: info.vin,
                user_vehicle_id: info.id,
              }}
              verificationCode={verificationCode}
              isVehicleDataEffort={isVehicleDataEffort}
            />
            <Flex justify="center" mt="lg">
              <Button
                size="xs"
                color="gray"
                variant="subtle"
                onClick={() => {
                  setOpen(false);
                  fetchData();
                }}
              >
                {t("common:close")}
              </Button>
            </Flex>
          </motion.div>
        ) : (
          <div>
            {allowMileageEntry && (
              <>
                {info.has_unverified_readings ? (
                  <Button
                    fullWidth
                    radius="xl"
                    size="sm"
                    mt="xs"
                    onClick={setUnverifiedView}
                    color="yellow"
                  >
                    {t("mixer:vehicle:verify_reading")}
                  </Button>
                ) : (
                  <Button
                    fullWidth
                    radius="xl"
                    size="sm"
                    mt="xs"
                    onClick={() => setOpen(true)}
                  >
                    {t("mixer:vehicle:log_units_button", {
                      units: info.use_kilometers ? "Kilometers" : "Miles",
                    })}
                  </Button>
                )}
              </>
            )}
          </div>
        )}
      </Box>
    </Card>
  );
};

const AddNewReading = ({
  reqData,
  fetchData,
  verificationCode,
  codeVerificationData,
  currentOdometer,
  odometerRolled,
  isVehicleDataEffort = false,
}) => {
  const { t } = useTranslation();
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [dealerCodeData, setDealerCodeData] = useState(null);
  const [unverifiedReading, setUnverifiedReading] = useState(null);
  const [dealerSubmitting, setDealerSubmitting] = useState(false);
  const [userSubmittedReading, setUserSubmittedReading] = useState("");
  const [odometerMismatchModalOpen, setOdometerMismatchModalOpen] =
    useState(false);

  useEffect(() => {
    if (verificationCode) {
      setDealerCodeData(verificationCode);
    }
  }, []);

  const valueIsLower = odometerRolled
    ? value && parseInt(value) <= parseInt(currentOdometer - 1000000)
    : value && parseInt(value) <= parseInt(currentOdometer);
  const formattedValue = odometerRolled ? parseInt(value) + 1000000 : value;

  function onSubmit() {
    const req = {
      ...reqData,
      odometer_value: formattedValue,
    };
    setUserSubmittedReading(value.replace(/^0+/, ""));

    setLoading(true);

    axios
      .post(`/ride-challenge/submit-additional-reading/`, req)
      .then(({ data }) => {
        const res = data.response[0];
        if (verificationCode) {
          onDealerSubmit(res);
        } else {
          setLoading(false);
          setUnverifiedReading(res);
          setValue("");
        }
      })
      .catch((err) => {
        toast.error(err);
        setLoading(false);
      });
  }

  function resolveOdometerMismatch(val) {
    setValue(val);
    setOdometerMismatchModalOpen(false);
    onDealerSubmit(null, val);
  }

  function onDealerSubmit(
    unverifiedResponseData = null,
    odometerValueOverride = null
  ) {
    const odometerValue =
      odometerValueOverride !== null ? odometerValueOverride : value;

    if (userSubmittedReading !== odometerValue && !odometerMismatchModalOpen) {
      setOdometerMismatchModalOpen(true);
      return;
    }

    setDealerSubmitting(true);

    const formattedValue = odometerRolled
      ? parseInt(odometerValue) + 1000000
      : odometerValue;

    const req = {
      ...reqData,
      verifier_remote_code_id: dealerCodeData.id,
      odometer_value: formattedValue,
      ride_challenge_data_point_id:
        unverifiedReading?.ride_challenge_data_point_id ||
        unverifiedResponseData?.ride_challenge_data_point_id,
    };

    axios
      .post(`/ride-challenge/verify-reading/`, req)
      .then(() => {
        toast.success(t("common:verified") + "!");
        setDealerSubmitting(false);
        fetchData();
        // fetchUser();
      })
      .catch((err) => {
        toast.error(err);
        setDealerSubmitting(false);
      });
  }

  if (dealerCodeData) {
    return (
      <Box mt="lg">
        <DealerMode dealerCodeData={dealerCodeData}>
          <DealerWarnings
            reqData={{
              campaign_effort_id: reqData.campaign_effort_id,
              user_id: reqData.user_id,
            }}
          />
          <Flex justify="center" mt="lg">
            <OdometerForm value={value} onChange={(e) => setValue(e)} />
          </Flex>
          {unverifiedReading && (
            <OdometerValueMismatch
              originalValue={userSubmittedReading}
              newValue={`${value}`}
            />
          )}
          <Flex justify="center" mt="lg">
            <Button
              onClick={() => {
                if (verificationCode) {
                  onSubmit();
                } else {
                  onDealerSubmit();
                }
              }}
              disabled={!value || valueIsLower}
              loading={dealerSubmitting}
            >
              {t("mixer:vehicle:submit_reading")}
            </Button>
          </Flex>
          {odometerMismatchModalOpen && (
            <OdometerMismatchModal
              userValue={userSubmittedReading}
              dealerValue={value}
              onClose={() => setOdometerMismatchModalOpen(false)}
              onSubmit={(val) => resolveOdometerMismatch(val)}
            />
          )}
        </DealerMode>
      </Box>
    );
  }

  if (unverifiedReading) {
    return (
      <Box mt="lg">
        <DealerVerification
          codeVerificationData={codeVerificationData}
          setDealerCodeData={(e) => setDealerCodeData(e)}
        />
      </Box>
    );
  }

  return (
    <div>
      <Flex justify="center" mt="lg">
        <OdometerForm value={value} onChange={(e) => setValue(e)} />
      </Flex>
      <Flex justify="center" mt="lg">
        <Button
          onClick={onSubmit}
          disabled={!value || valueIsLower}
          loading={loading}
        >
          {t("common:submit")}
        </Button>
      </Flex>
    </div>
  );
};
